import { useState } from "react";
import axios from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, IconButton, Stack, Tooltip } from "@mui/material";
import LockResetIcon from "@mui/icons-material/LockReset";
import GenericFormPopup from "../Fleet/GenericFormPopup/GenericFormPopup";
import StatusSnackbar from "../General/StatusSnackbar/StatusSnackbar";
import { apiVariable } from "../../api/apiVariable";
import { getPublicApiError } from "../../helpers/publicRequestError";
import type { UserDto } from "./UsersTable";

const initialValues = { resetCode: "", password: "", confirm: "" };

const ResetPasswordPopup: React.FC<{ user: UserDto }> = ({ user }) => {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [codeRequested, setCodeRequested] = useState(false);
  const [formError, setFormError] = useState("");
  const [success, setSuccess] = useState(false);

  const requestCode = useMutation({
    mutationFn: () =>
      axios.post(`${apiVariable}/forgotPassword`, {
        userName: user.username,
      }),
    onSuccess: () => {
      setCodeRequested(true);
      setFormError("");
    },
    onError: (error) =>
      setFormError(
        getPublicApiError(
          error,
          "Unable to request a reset code. Please try again.",
        ),
      ),
  });

  const resetPassword = useMutation({
    mutationFn: ({ resetCode, password }: typeof initialValues) =>
      axios.post(`${apiVariable}/resetPassword`, {
        userName: user.username,
        resetCode: resetCode.trim(),
        newPassword: password,
      }),
    onSuccess: () => {
      setSuccess(true);
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (error) =>
      setFormError(
        getPublicApiError(
          error,
          "Unable to reset the password. Check the emailed code and password requirements, or request a new code.",
        ),
      ),
  });

  const busy = requestCode.isPending || resetPassword.isPending;
  const handleSubmit = (values: typeof initialValues) => {
    if (busy) return;
    if (values.password !== values.confirm) {
      setFormError("Passwords don't match.");
      return;
    }
    setFormError("");
    resetPassword.mutate(values);
  };

  return (
    <>
      <Tooltip title="Reset password">
        <IconButton
          size="small"
          aria-label={`Reset password for ${user.username}`}
          onClick={() => {
            setCodeRequested(false);
            setFormError("");
            setOpen(true);
          }}
        >
          <LockResetIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <GenericFormPopup
        open={open}
        title={`Reset password for “${user.username}”`}
        initialValues={initialValues}
        formFields={[
          {
            name: "resetCode",
            label: "Code from recovery email",
            type: "text",
            validation: {
              required: "Enter the code from the recovery email.",
              maxLength: { value: 4096, message: "The code is too long." },
              validate: (value: string) =>
                !!value.trim() || "Enter the code from the recovery email.",
            },
          },
          {
            name: "password",
            label: "New password",
            type: "password",
            validation: {
              required: "Enter a new password.",
              maxLength: { value: 1024, message: "The password is too long." },
            },
          },
          {
            name: "confirm",
            label: "Confirm password",
            type: "password",
            validation: {
              required: "Confirm the new password.",
            },
          },
        ]}
        additionalComponents={
          <Stack spacing={2} alignItems="flex-start">
            <Alert severity="info">
              {codeRequested
                ? "If this account has a verified email address, a reset code will be sent there. Ask the account owner for the code."
                : "Request a code at the account's verified email address, then enter it above. If no email arrives, contact your administrator."}
            </Alert>
            <Button
              type="button"
              disabled={busy}
              onClick={() => requestCode.mutate()}
            >
              {requestCode.isPending
                ? "Requesting code…"
                : codeRequested
                  ? "Request a new code"
                  : "Email reset code"}
            </Button>
            {formError && <Alert severity="error">{formError}</Alert>}
          </Stack>
        }
        onSubmit={handleSubmit}
        onClose={() => {
          if (!busy) setOpen(false);
        }}
        loading={busy}
      />
      <StatusSnackbar
        status={success ? "success" : "idle"}
        successMessage="Password reset!"
        errorMessage=""
        handleClose={() => setSuccess(false)}
      />
    </>
  );
};

export default ResetPasswordPopup;
